/**
 * Custom game-styled dropdowns — OS-independent look, hover/select sounds.
 */

import { playUiButtonHover, playUiToggleOn } from "./ui-sounds";

type GameSelectState = {
    wrap: HTMLElement;
    trigger: HTMLButtonElement;
    panel: HTMLDivElement;
    open: boolean;
};

const stateBySelect = new WeakMap<HTMLSelectElement, GameSelectState>();
let mounted = false;
let openSelect: HTMLSelectElement | null = null;

function labelForOption(opt: HTMLOptionElement): string {
    return opt.textContent?.trim() || opt.value || "—";
}

function fitGameSelectLabel(el: HTMLElement, minPx = 9) {
    el.style.fontSize = "";
    let size = Number.parseFloat(getComputedStyle(el).fontSize) || 12;
    const maxW = el.clientWidth;
    if (maxW < 4) return;
    let guard = 0;
    while (el.scrollWidth > maxW && size > minPx && guard < 24) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
        guard++;
    }
}

function syncTriggerLabel(select: HTMLSelectElement, trigger: HTMLButtonElement) {
    const opt = select.selectedOptions[0] ?? select.options[select.selectedIndex];
    trigger.textContent = opt ? labelForOption(opt) : "—";
    trigger.disabled = select.disabled;
    trigger.setAttribute("aria-expanded", stateBySelect.get(select)?.open ? "true" : "false");
    requestAnimationFrame(() => fitGameSelectLabel(trigger));
}

function rebuildPanel(select: HTMLSelectElement, panel: HTMLDivElement) {
    panel.replaceChildren();
    for (const opt of [...select.options]) {
        if (opt.disabled && !opt.value) continue;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "gameSelectOption";
        btn.textContent = labelForOption(opt);
        btn.dataset.value = opt.value;
        btn.setAttribute("role", "option");
        btn.setAttribute("aria-selected", opt.selected ? "true" : "false");
        if (opt.disabled) {
            btn.disabled = true;
            btn.classList.add("gameSelectOption--disabled");
        }
        if (opt.selected) btn.classList.add("gameSelectOption--selected");
        btn.addEventListener("pointerenter", () => {
            if (!btn.disabled) playUiButtonHover();
        });
        btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (opt.disabled || opt.value === select.value) {
                closeGameSelect(select);
                return;
            }
            select.value = opt.value;
            syncTriggerLabel(select, stateBySelect.get(select)!.trigger);
            rebuildPanel(select, panel);
            playUiToggleOn();
            select.dispatchEvent(new Event("change", { bubbles: true }));
            closeGameSelect(select);
        });
        panel.appendChild(btn);
    }
    requestAnimationFrame(() => {
        panel.querySelectorAll<HTMLElement>(".gameSelectOption").forEach((btn) => fitGameSelectLabel(btn));
    });
}

function dockPanel(st: GameSelectState) {
    st.panel.classList.remove("gameSelectPanel--floating");
    st.panel.style.position = "";
    st.panel.style.left = "";
    st.panel.style.top = "";
    st.panel.style.width = "";
    st.panel.style.zIndex = "";
    if (st.panel.parentElement !== st.wrap) {
        st.wrap.appendChild(st.panel);
    }
}

function floatPanel(select: HTMLSelectElement, st: GameSelectState) {
    const rect = st.trigger.getBoundingClientRect();
    st.panel.classList.add("gameSelectPanel--floating");
    document.body.appendChild(st.panel);
    st.panel.style.position = "fixed";
    st.panel.style.left = `${rect.left}px`;
    st.panel.style.top = `${rect.bottom + 4}px`;
    st.panel.style.width = `${Math.max(rect.width, 120)}px`;
    st.panel.style.zIndex = "12000";
    requestAnimationFrame(() => {
        fitGameSelectLabel(st.trigger);
        st.panel.querySelectorAll<HTMLElement>(".gameSelectOption").forEach((btn) => fitGameSelectLabel(btn));
    });
}

function closeGameSelect(select: HTMLSelectElement | null) {
    if (!select) return;
    const st = stateBySelect.get(select);
    if (!st) return;
    st.open = false;
    st.panel.classList.add("hidden");
    st.trigger.setAttribute("aria-expanded", "false");
    dockPanel(st);
    if (openSelect === select) openSelect = null;
}

function openGameSelect(select: HTMLSelectElement) {
    if (select.disabled) return;
    if (openSelect && openSelect !== select) closeGameSelect(openSelect);
    const st = stateBySelect.get(select);
    if (!st) return;
    st.open = true;
    rebuildPanel(select, st.panel);
    floatPanel(select, st);
    st.panel.classList.remove("hidden");
    st.trigger.setAttribute("aria-expanded", "true");
    openSelect = select;
    playUiButtonHover();
}

function enhanceSelect(select: HTMLSelectElement) {
    if (select.dataset.gameSelect === "1") return;
    select.dataset.gameSelect = "1";

    const wrap = document.createElement("div");
    wrap.className = "gameSelect";
    if (select.classList.contains("modesFilterSelect")) wrap.classList.add("gameSelect--modesFilter");
    if (select.classList.contains("statsSpeedrunLevelSelect") || select.classList.contains("statsPenaltyLevelSelect")) {
        wrap.classList.add("gameSelect--stats");
    }
    select.parentNode?.insertBefore(wrap, select);
    wrap.appendChild(select);

    select.classList.add("gameSelectNative");

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "gameSelectTrigger formInput";
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");
    if (select.id) trigger.setAttribute("aria-labelledby", select.id);
    if (select.getAttribute("aria-label")) {
        trigger.setAttribute("aria-label", select.getAttribute("aria-label")!);
    }

    const panel = document.createElement("div");
    panel.className = "gameSelectPanel hidden";
    panel.setAttribute("role", "listbox");

    wrap.appendChild(trigger);
    wrap.appendChild(panel);

    const st: GameSelectState = { wrap, trigger, panel, open: false };
    stateBySelect.set(select, st);

    syncTriggerLabel(select, trigger);
    rebuildPanel(select, panel);

    trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (st.open) closeGameSelect(select);
        else openGameSelect(select);
    });

    select.addEventListener("change", () => {
        syncTriggerLabel(select, trigger);
        rebuildPanel(select, panel);
    });

    const mo = new MutationObserver(() => {
        syncTriggerLabel(select, trigger);
        if (st.open) rebuildPanel(select, panel);
    });
    mo.observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
}

/** Append select (or its custom wrap) without breaking the game-select UI. */
export function appendGameSelectHost(parent: HTMLElement, select: HTMLSelectElement | null | undefined) {
    if (!select || !parent) return;
    if (select.dataset.gameSelect !== "1") enhanceSelect(select);
    const host = (select.closest(".gameSelect") as HTMLElement | null) ?? select;
    parent.appendChild(host);
    refreshGameSelect(select);
}

export function refreshGameSelect(select: HTMLSelectElement | null | undefined) {
    if (!select) return;
    if (select.dataset.gameSelect !== "1") enhanceSelect(select);
    const st = stateBySelect.get(select);
    if (!st) return;
    syncTriggerLabel(select, st.trigger);
    rebuildPanel(select, st.panel);
}

function scanAndEnhance(root: ParentNode = document) {
    root.querySelectorAll<HTMLSelectElement>("select:not([data-game-select='1']):not([data-game-select-defer='1'])").forEach(enhanceSelect);
}

export function mountGameSelects() {
    if (mounted) return;
    mounted = true;

    scanAndEnhance();

    const domObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (node instanceof HTMLSelectElement) enhanceSelect(node);
                else if (node instanceof HTMLElement) scanAndEnhance(node);
            }
        }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });

    document.addEventListener(
        "click",
        (e) => {
            if (!(e.target instanceof Element)) return;
            if (e.target.closest(".gameSelect") || e.target.closest(".gameSelectPanel--floating")) return;
            closeGameSelect(openSelect);
        },
        true,
    );

    window.addEventListener(
        "scroll",
        (e) => {
            if (!openSelect) return;
            const t = e.target;
            if (t instanceof Element && t.closest(".gameSelectPanel--floating")) return;
            closeGameSelect(openSelect);
        },
        true,
    );

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeGameSelect(openSelect);
    });
}
